import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import { merge, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { AssetsType } from 'src/app/constants/assetsType';
import { AssetsService } from 'src/app/services/assets.service';

@Component({
  selector: 'app-new-asset',
  templateUrl: './new-asset.component.html',
  styleUrls: ['./new-asset.component.css'],
})
export class NewAssetComponent implements OnInit, OnDestroy {
  assetsList = AssetsType;
  selectedAsset: string;
  selectedAssetDescription: string;
  employeeName: any[] = [];
  assetType;
  disableBtn = false;
  assetId: any;
  successFlag = false;
  description: string;
  empName: any;
  assetCategorySelected: any;
  assetValidationError = '';
  isCheckingModelSerial = false;
  validationSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private assetsService: AssetsService,
  ) {}

  ngOnInit(): void {
    // Initialize form and subscribe to checkbox changes
    this.assetsForm.get('insurance')?.valueChanges.subscribe(() => {
      this.updateFormValidators();
    });
    this.assetsForm.get('warranty')?.valueChanges.subscribe(() => {
      this.updateFormValidators();
    });
  }

  ngOnDestroy(): void {
    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
    }
  }

  onSelectionChange(event: any) {
    let asset = event.target.value;
    this.assetType = asset;
    this.selectedAsset = asset;
    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
    }
    if (this.selectedAsset === 'Car') {
      this.setupCarValidation();
    } else if (
      ['Laptop', 'Desktop', 'Mobile', 'Monitor', 'Other'].includes(
        this.selectedAsset,
      )
    ) {
      this.setupElectronicValidation();
    }
    this.updateFormValidators();
  }

  updateFormValidators() {
    const carGroup = this.assetsForm.get('assetCar') as FormGroup;
    const electronicGroup = this.assetsForm.get('assetElectronic') as FormGroup;

    if (!carGroup || !electronicGroup) {
      return;
    }

    // Clear validators
    Object.keys(carGroup.controls).forEach((key) => {
      const control = carGroup.get(key);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
      }
    });
    Object.keys(electronicGroup.controls).forEach((key) => {
      const control = electronicGroup.get(key);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
      }
    });

    // Add validators based on asset type
    if (this.selectedAsset === 'Car') {
      Object.keys(carGroup.controls).forEach((key) => {
        const control = carGroup.get(key);
        if (control) {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    } else if (
      ['Laptop', 'Desktop', 'Mobile', 'Monitor', 'Other'].includes(
        this.selectedAsset,
      )
    ) {
      Object.keys(electronicGroup.controls).forEach((key) => {
        const control = electronicGroup.get(key);
        if (control) {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    }

    // Handle conditional fields
    const mobileControl = this.assetsForm.get('mobileNumber');
    const fuelCardControl = this.assetsForm.get('fuelCard');
    const empAssignedDateControl = this.assetsForm.get('empAssignedDate');
    const commentsControl = this.assetsForm.get('comments');
    const assetStartDateControl = this.assetsForm.get('assetStartDate');

    if (this.selectedAsset === 'SIM' || this.selectedAsset === 'Fuel Card') {
      empAssignedDateControl?.clearValidators();
      commentsControl?.clearValidators();
      assetStartDateControl?.clearValidators();
    } else {
      empAssignedDateControl?.setValidators([Validators.required]);
      commentsControl?.setValidators([Validators.required]);
      assetStartDateControl?.setValidators([Validators.required]);
    }
    empAssignedDateControl?.updateValueAndValidity({ emitEvent: false });
    commentsControl?.updateValueAndValidity({ emitEvent: false });
    assetStartDateControl?.updateValueAndValidity({ emitEvent: false });

    if (mobileControl) {
      if (this.selectedAsset === 'SIM') {
        mobileControl.setValidators([Validators.required]);
      } else {
        mobileControl.clearValidators();
      }
      mobileControl.updateValueAndValidity({ emitEvent: false });
    }

    if (fuelCardControl) {
      if (this.selectedAsset === 'Fuel Card') {
        fuelCardControl.setValidators([Validators.required]);
      } else {
        fuelCardControl.clearValidators();
      }
      fuelCardControl.updateValueAndValidity({ emitEvent: false });
    }

    // Handle insurance conditional fields
    const insuStartControl = this.assetsForm.get('insuStartDate');
    const insuEndControl = this.assetsForm.get('insuEndDate');
    if (this.assetsForm.get('insurance')?.value) {
      insuStartControl?.setValidators([Validators.required]);
      insuEndControl?.setValidators([Validators.required]);
    } else {
      insuStartControl?.clearValidators();
      insuEndControl?.clearValidators();
    }
    insuStartControl?.updateValueAndValidity({ emitEvent: false });
    insuEndControl?.updateValueAndValidity({ emitEvent: false });

    // Handle warranty conditional fields
    const warStartControl = this.assetsForm.get('warStartDate');
    const warEndControl = this.assetsForm.get('warEndDate');
    if (this.assetsForm.get('warranty')?.value) {
      warStartControl?.setValidators([Validators.required]);
      warEndControl?.setValidators([Validators.required]);
    } else {
      warStartControl?.clearValidators();
      warEndControl?.clearValidators();
    }
    warStartControl?.updateValueAndValidity({ emitEvent: false });
    warEndControl?.updateValueAndValidity({ emitEvent: false });

    this.assetsForm.updateValueAndValidity({ emitEvent: false });
    this.validateAssetIfReady();

    // Handle lease end date validation based on category
    const leaseEndDateControl = this.assetsForm.get('assetCar.leaseEndDate');
    if (this.assetCategorySelected === 'Leased') {
      leaseEndDateControl?.setValidators([Validators.required]);
    } else {
      leaseEndDateControl?.clearValidators();
    }
    leaseEndDateControl?.updateValueAndValidity({ emitEvent: false });
  }

  setupCarValidation() {
    const carNumberControl = this.assetsForm.get('assetCar.number');
    if (!carNumberControl) return;
    this.validationSubscription = carNumberControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((carNumber) => {
          if (!carNumber) {
            this.assetValidationError = '';
            return of({ responseStatus: 'Failure' });
          }
          this.isCheckingModelSerial = true;
          return this.assetsService.validateCarAsset(carNumber).pipe(
            catchError(() => of({ responseStatus: 'Failure' })),
            finalize(() => {
              this.isCheckingModelSerial = false;
            }),
          );
        }),
      )
      .subscribe((res: any) => {
        this.applyAssetValidation(res);
      });
  }

  setupElectronicValidation() {
    const modelControl = this.assetsForm.get('assetElectronic.electronicModel');
    const serialControl = this.assetsForm.get(
      'assetElectronic.electronicSerialNumber',
    );
    if (!modelControl || !serialControl) return;
    this.validationSubscription = merge(
      modelControl.valueChanges,
      serialControl.valueChanges,
    )
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() => {
          const model = modelControl.value;
          const serial = serialControl.value;
          if (!model || !serial) {
            this.assetValidationError = '';
            return of({ responseStatus: 'Failure' });
          }
          this.isCheckingModelSerial = true;
          return this.assetsService.validateElectronicAsset(model, serial).pipe(
            catchError(() => of({ responseStatus: 'Failure' })),
            finalize(() => {
              this.isCheckingModelSerial = false;
            }),
          );
        }),
      )
      .subscribe((res: any) => {
        this.applyAssetValidation(res);
      });
  }

  validateAssetIfReady() {
    if (this.selectedAsset === 'Car') {
      const carNumber = this.assetsForm.get('assetCar.number')?.value;
      if (carNumber) {
        this.isCheckingModelSerial = true;
        this.assetsService
          .validateCarAsset(carNumber)
          .pipe(
            catchError(() => of({ responseStatus: 'Failure' })),
            finalize(() => {
              this.isCheckingModelSerial = false;
            }),
          )
          .subscribe((res: any) => {
            this.applyAssetValidation(res);
          });
      } else {
        this.assetValidationError = '';
      }
      return;
    }

    if (
      ['Laptop', 'Desktop', 'Mobile', 'Monitor', 'Other'].includes(
        this.selectedAsset,
      )
    ) {
      const model = this.assetsForm.get(
        'assetElectronic.electronicModel',
      )?.value;
      const serial = this.assetsForm.get(
        'assetElectronic.electronicSerialNumber',
      )?.value;

      if (model && serial) {
        this.isCheckingModelSerial = true;
        this.assetsService
          .validateElectronicAsset(model, serial)
          .pipe(
            catchError(() => of({ responseStatus: 'Failure' })),
            finalize(() => {
              this.isCheckingModelSerial = false;
            }),
          )
          .subscribe((res: any) => {
            this.applyAssetValidation(res);
          });
      } else {
        this.assetValidationError = '';
      }
      return;
    }

    this.assetValidationError = '';
  }

  applyAssetValidation(res: any) {
    const serialControl = this.assetsForm.get(
      'assetElectronic.electronicSerialNumber',
    );
    const carNumberControl = this.assetsForm.get('assetCar.number');

    // For car assets, if responseStatus is not "Failure", it means the asset exists (invalid)
    // For electronic assets, use the existing logic
    let isValid: boolean;
    if (res && res.responseStatus !== undefined) {
      isValid = res.responseStatus === 'Failure';
    } else {
      isValid =
        res &&
        (res.valid !== undefined
          ? res.valid
          : res.isValid !== undefined
            ? res.isValid
            : true);
    }

    if (!isValid) {
      const message =
        res?.message ||
        res?.errorDescription ||
        (this.selectedAsset === 'Car'
          ? 'Car number is already assigned to an employee.'
          : 'Model and serial number are already assigned to an employee.');
      this.assetValidationError = message;
      this.disableBtn = true;
      if (this.selectedAsset === 'Car' && carNumberControl) {
        carNumberControl.setErrors({ modelSerialInvalid: true });
      }
      if (
        ['Laptop', 'Desktop', 'Mobile', 'Monitor', 'Other'].includes(
          this.selectedAsset,
        ) &&
        serialControl
      ) {
        serialControl.setErrors({ modelSerialInvalid: true });
      }
    } else {
      this.assetValidationError = '';
      this.disableBtn = false;
      if (carNumberControl) {
        const errors = carNumberControl.errors;
        if (errors) {
          delete errors.modelSerialInvalid;
          if (Object.keys(errors).length > 0) {
            carNumberControl.setErrors(errors);
          } else {
            carNumberControl.setErrors(null);
          }
        }
      }
      if (serialControl) {
        const errors = serialControl.errors;
        if (errors) {
          delete errors.modelSerialInvalid;
          if (Object.keys(errors).length > 0) {
            serialControl.setErrors(errors);
          } else {
            serialControl.setErrors(null);
          }
        }
      }
    }
  }

  empNameSelected(emp: any) {
    this.empName = emp;
  }

  onSubmit() {
    console.log(this.assetsForm.value);
    this.assetsForm.patchValue({
      assetAssignedToEmp: this.empName,
    });
    const assetJson = JSON.stringify(this.assetsForm.value);
    console.log('assetJson ' + assetJson);

    this.assetsService.createAsset(assetJson).subscribe((data) => {
      console.log('data ==========> ' + data);
      let serviceResponse = data;
      this.assetId = data.assetId;
      this.successFlag = true;

      if (data.responseStatus == 'Failure') {
        this.description = data.errorDescription;
      } else {
        this.description =
          'New Asset created successfully !! Asset id is ' + data.assetId;
        this.assetsForm.reset();
      }
    });
  }

  assetsForm = this.fb.group({
    assetType: ['', Validators.required],
    assetAssignedToEmp: [''],
    empAssignedDate: ['', Validators.required],
    comments: ['', Validators.required],
    mobileNumber: [''],
    fuelCard: [''],
    assetStartDate: ['', Validators.required],
    assetEndDate: [''],
    insurance: [false],
    insuStartDate: [''],
    insuEndDate: [''],
    warranty: [false],
    warStartDate: [''],
    warEndDate: [''],
    assetCar: this.fb.group({
      model: [''],
      make: [''],
      number: [''],
      color: [''],
      chasisNumber: [''],
      carbonEmission: [],
      fuelType: [''],
      cataloguePrice: [''],
      assetCategory: [''],
      leaseEndDate: [''],
    }),
    assetElectronic: this.fb.group({
      electronicModel: [''],
      electronicSerialNumber: [''],
      electronicPrice: [''],
    }),
  });
}
